/**
 * The rhythm of a Mystic's birth.
 *
 * The pipeline takes about seventy seconds and its own progress cannot carry a
 * screen: `current_step` changes four times, with gaps of thirteen and fourteen
 * seconds. Driven by it, the reveal would stall twice and then dump everything
 * at once.
 *
 * So it keeps its own time. The chart is arithmetic — it arrives whole on the
 * first poll — which means the wait can be spent on things that are already
 * true, in the order a chart is actually read: the moment, then the luminaries,
 * then the contacts inside it, then how it meets its seeker.
 *
 * Two rules:
 *   the last beat HOLDS until the Mystic is genuinely finished; and
 *   it never jumps ahead to catch up. A reveal that skips to the end because the
 *   build came back early is the jankiest thing on a screen someone just paid
 *   for.
 */

// Milliseconds. Tuned against a measured 70s pipeline: the beats spend a little
// under forty seconds so the hold — which is graceful — covers the rest, rather
// than the beats racing a finish line they cannot see.
const OPENING = 600
const AFTER_STAMP = 2600
const BETWEEN_LUMINARIES = 2400
const BEFORE_ASPECTS = 3200
const BETWEEN_ASPECTS = 3000
const BEFORE_SYNASTRY = 3600
const BETWEEN_CONTACTS = 2800
const BEFORE_BOND = 3600

/**
 * The ordered beats for a chart, each with the moment it lands.
 *
 * `linesFrom` is the elapsed time at which the Mystic's own lines arrived. The
 * aspect and synastry beats are held until `chart.lines_spoken` is true — the
 * screen must never read the template aloud — and when they do come they are
 * scheduled from that moment, so a late arrival does not dump them all at once.
 * A payload from before the flag existed has no flag and is read as before.
 */
export function beatsFor (chart, { linesFrom = 0 } = {}) {
  if (!chart) return []
  const beats = []
  let at = OPENING

  beats.push({ kind: 'stamp', at, data: chart.born || {} })
  at += AFTER_STAMP

  // The luminaries and the rising, in the order anyone reads a chart aloud.
  for (const kind of ['sun', 'moon', 'rising']) {
    if (chart[kind]) {
      beats.push({ kind, at, data: { sign: chart[kind] } })
      at += BETWEEN_LUMINARIES
    }
  }

  const spoken = chart.lines_spoken !== false
  const aspects = spoken ? (chart.aspects || []) : []
  if (aspects.length) {
    at += BEFORE_ASPECTS - BETWEEN_LUMINARIES
    at = Math.max(at, linesFrom + OPENING)
    for (const a of aspects) {
      beats.push({ kind: 'aspect', at, data: a })
      at += BETWEEN_ASPECTS
    }
  }

  const synastry = spoken ? (chart.synastry || []) : []
  if (synastry.length) {
    at += BEFORE_SYNASTRY - BETWEEN_ASPECTS
    at = Math.max(at, linesFrom + OPENING)
    for (const c of synastry) {
      beats.push({ kind: 'synastry', at, data: c })
      at += BETWEEN_CONTACTS
    }
  }

  // The Mystic's one sentence about its maker. Written by the persona step,
  // so it arrives mid-wait; it is the last thing said before the face.
  if (chart.bond && spoken) {
    at += BEFORE_BOND - (synastry.length ? BETWEEN_CONTACTS : 0)
    beats.push({ kind: 'bond', at, data: chart.bond })
  }

  return beats
}

/** The beats that have landed by `elapsed` milliseconds. */
export function visibleBeats (beats, elapsed) {
  return (beats || []).filter(b => b.at <= elapsed)
}

/**
 * Where the reveal is.
 *
 * `done` requires both that the beats are spent AND that the Mystic is ready —
 * either alone leaves someone looking at an unfinished thing or at a finished
 * one they were never shown.
 */
export function revealState ({ beats, elapsed, ready }) {
  const list = beats || []
  const visible = visibleBeats(list, elapsed)
  const last = list.length ? list[list.length - 1].at : 0
  const spent = elapsed >= last
  return {
    visible,
    spent,
    done: Boolean(spent && ready),
    holding: Boolean(spent && !ready),
    heldFor: spent ? elapsed - last : 0,
  }
}
